import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { useRouter } from 'next/router';
import CloseIcon from '~/icons/close.svg';
import { useTranslation } from '~/shared/utils/translation';
import { StyledSearchCloseButton, StyledSearchInputDesktop } from '../N20MainMenu/styled';
import { SearchPlaceholderComponent } from './SearchPlaceholderComponent';
import { useSearchOverlay } from '~/services/search/useSearchOverlay';
import { isValidSearchTerm } from '~/services/instantsearch/hooks/useAlgoliaProductSearch';
import { StyledSearchForm } from './styled';
import { closeMenu } from '../N30MegaMenu/MegaMenu';

interface SearchFormProps {
    onSubmit?(query: string): void;
    getExactMatchRedirectUrl?: (query: string) => string | undefined;
}

export const SearchForm: React.FC<SearchFormProps> = ({ onSubmit }) => {
    const router = useRouter();
    const { translate } = useTranslation();
    const { setQuery, isOverlayVisible, setIsOverlayVisible } = useSearchOverlay();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const clearInput = (opts?: { keepFocus: boolean }) => {
        const { keepFocus = false } = opts || {};

        setSearchTerm('');

        setTimeout(() => {
            if (keepFocus) inputRef?.current?.focus();
            else inputRef?.current?.blur();
        });
    };

    const closeSearchForm = () => {
        clearInput();
        setIsOverlayVisible(false);
    };

    const openSearchForm = () => {
        // Avoid the mega menu staying open and overlaying the search results page.
        closeMenu();
        if (!isOverlayVisible) setIsOverlayVisible(true);
    };

    const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setSearchTerm(value);
    }, []);

    const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') closeSearchForm();
    };

    const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!searchTerm) return;

        onSubmit?.(searchTerm);
        closeSearchForm();
    };

    useDebounce(
        () => {
            if (!searchTerm || isValidSearchTerm(searchTerm)) {
                setQuery(searchTerm);
            }
        },
        350,
        [searchTerm],
    );

    // Clear input when navigating away from page.
    useEffect(() => {
        router.events.on('routeChangeStart', clearInput);

        return () => {
            router.events.off('routeChangeStart', clearInput);
        };
    }, []);

    return (
        <StyledSearchForm onSubmit={onFormSubmit} autoComplete="off">
            <SearchPlaceholderComponent
                icon={isOverlayVisible ? 'arrowLeft' : 'search'}
                onClick={closeSearchForm}
            >
                {!isOverlayVisible && translate('header.search')}
            </SearchPlaceholderComponent>

            <StyledSearchInputDesktop
                type="search"
                aria-label="Search"
                name="query"
                value={searchTerm}
                onChange={onInputChange}
                onKeyDown={onInputKeyDown}
                onClick={openSearchForm}
                onFocus={openSearchForm}
                withLabel={false}
                ref={inputRef}
                data-testid="search-input"
            />

            {/* This will allow us to submit the form using "Enter" or similar keys. */}
            <input type="submit" hidden />

            {!!searchTerm && (
                <StyledSearchCloseButton
                    aria-label="Ryd søgning"
                    size="small"
                    onClick={() => clearInput({ keepFocus: true })}
                    variant="plain"
                >
                    <CloseIcon />
                </StyledSearchCloseButton>
            )}
        </StyledSearchForm>
    );
};
